/** Describes which network path a peer connection ended up using, read from WebRTC statistics. */
import { t } from './i18n/index.js';

export type RouteKind = 'direct' | 'reflexive' | 'relay' | 'unknown';

export type RouteInfo = Readonly<{
  kind: RouteKind;
  /** Transport between the relaying peer and the TURN server, when relayed. */
  relayProtocol: string | null;
  rttMs: number | null;
}>;

export const UNKNOWN_ROUTE: RouteInfo = { kind: 'unknown', relayProtocol: null, rttMs: null };

type CandidateType = 'host' | 'srflx' | 'prflx' | 'relay';

export type CandidateSummary = Readonly<{
  type: CandidateType;
  relayProtocol: string | null;
}>;

export const classifyRoute = (local: CandidateSummary, remote: CandidateSummary, rttMs: number | null): RouteInfo => {
  if (local.type === 'relay' || remote.type === 'relay') {
    return { kind: 'relay', relayProtocol: local.relayProtocol ?? remote.relayProtocol, rttMs };
  }
  if (local.type === 'host' && remote.type === 'host') {
    return { kind: 'direct', relayProtocol: null, rttMs };
  }
  return { kind: 'reflexive', relayProtocol: null, rttMs };
};

export const formatRoute = (route: RouteInfo): string => {
  const rtt = route.rttMs === null ? '' : ` · ${Math.round(route.rttMs)} ms`;
  switch (route.kind) {
    case 'direct':
      return `${t('route.direct')}${rtt}`;
    case 'reflexive':
      return `${t('route.nat')}${rtt}`;
    case 'relay':
      return `${route.relayProtocol === null ? t('route.relay') : t('route.relayOver', { protocol: route.relayProtocol })}${rtt}`;
    case 'unknown':
      return t('route.finding');
  }
};

type CandidateStats = RTCStats & { candidateType?: CandidateType; relayProtocol?: string };
type PairStats = RTCStats & {
  state?: string;
  nominated?: boolean;
  localCandidateId?: string;
  remoteCandidateId?: string;
  currentRoundTripTime?: number;
};

const summarize = (candidate: CandidateStats | undefined): CandidateSummary | null =>
  candidate?.candidateType === undefined ? null : { type: candidate.candidateType, relayProtocol: candidate.relayProtocol ?? null };

/** Reads the nominated candidate pair from the connection's statistics report. */
export const readRoute = async (pc: RTCPeerConnection): Promise<RouteInfo> => {
  const report = await pc.getStats();
  const candidates = new Map<string, CandidateStats>();
  let pair: PairStats | null = null;
  report.forEach((stats: RTCStats) => {
    if (stats.type === 'local-candidate' || stats.type === 'remote-candidate') {
      candidates.set(stats.id, stats as CandidateStats);
    }
    if (stats.type === 'candidate-pair') {
      const candidate = stats as PairStats;
      if (candidate.state === 'succeeded' && candidate.nominated === true) {
        pair = candidate;
      }
    }
  });
  if (pair === null) {
    return UNKNOWN_ROUTE;
  }
  const chosen: PairStats = pair;
  const local = summarize(chosen.localCandidateId === undefined ? undefined : candidates.get(chosen.localCandidateId));
  const remote = summarize(chosen.remoteCandidateId === undefined ? undefined : candidates.get(chosen.remoteCandidateId));
  if (local === null || remote === null) {
    return UNKNOWN_ROUTE;
  }
  return classifyRoute(local, remote, chosen.currentRoundTripTime === undefined ? null : chosen.currentRoundTripTime * 1000);
};

export const ROUTE_POLL_INTERVAL_MS = 2000;

/** Polls the route while a transfer runs; returns a function that stops polling. */
export const watchRoute = (pc: RTCPeerConnection, onRoute: (route: RouteInfo) => void): (() => void) => {
  let active = true;
  const tick = (): void => {
    if (!active) {
      return;
    }
    readRoute(pc)
      .then((route) => {
        if (active) {
          onRoute(route);
        }
      })
      .catch((error: unknown) => {
        throw new Error(`Reading connection statistics failed: ${String(error)}`);
      });
  };
  tick();
  const timer = window.setInterval(tick, ROUTE_POLL_INTERVAL_MS);
  return () => {
    active = false;
    window.clearInterval(timer);
  };
};
